import { createI18n } from 'vue-i18n'
import localizationService from '@/services/localizationService'

/**
 * UI texts are not bundled: they come from the API (AppLocalizationTexts) at startup and
 * whenever the language changes, so an edit on the admin screen reaches users without a
 * release.
 *
 * The last texts received per language are kept in localStorage. That copy is only a
 * safety net for when the API cannot be reached — the screen then shows yesterday's
 * wording instead of raw keys. It is never read when the API answers.
 */

export const DEFAULT_LOCALE = 'en'
const STORAGE_KEY = 'locale'
const CACHE_PREFIX = 'i18n_texts_'
const SYNCED_KEY = 'locale_synced'

const readStoredLocale = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_LOCALE
  } catch {
    return DEFAULT_LOCALE
  }
}

/**
 * { 'auth.register.title': 'x' } → { auth: { register: { title: 'x' } } }.
 * A key that is also the parent of other keys ('a.b' next to 'a.b.c') cannot exist in
 * vue-i18n's nested form; the parent wins and the leaf is reported once.
 */
export const unflatten = flat => {
  const root = {}
  const sortedKeys = Object.keys(flat).sort((a, b) => a.length - b.length || a.localeCompare(b))

  for (const key of sortedKeys) {
    const parts = key.split('.')
    let node = root
    let blocked = false

    for (const part of parts.slice(0, -1)) {
      if (typeof node[part] === 'string') {
        blocked = true
        break
      }
      if (node[part] === undefined) node[part] = {}
      node = node[part]
    }

    const leaf = parts[parts.length - 1]
    if (blocked || typeof node[leaf] === 'object') {
      console.warn(`[i18n] Key "${key}" collides with another key's path and is ignored`)
      continue
    }
    node[leaf] = flat[key]
  }

  return root
}

const i18n = createI18n({
  legacy: false,
  locale: readStoredLocale(),
  // The API already merges the default language under every other one.
  fallbackLocale: false,
  missingWarn: false,
  fallbackWarn: false,
  messages: {}
})

const loadedLocales = new Set()

const readCache = locale => {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + locale)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const writeCache = (locale, flat) => {
  try {
    localStorage.setItem(CACHE_PREFIX + locale, JSON.stringify(flat))
  } catch {
    // Full or blocked storage only loses the offline copy.
  }
}

/** Fetches one language's texts into i18n. Falls back to the stored copy if the API fails. */
export const loadLocaleMessages = async locale => {
  let flat
  try {
    flat = await localizationService.getTexts(locale)
    writeCache(locale, flat)
  } catch (err) {
    flat = readCache(locale)
    if (!flat) throw err
    console.warn(`[i18n] API unreachable, using stored texts for "${locale}"`, err)
  }

  i18n.global.setLocaleMessage(locale, unflatten(flat))
  loadedLocales.add(locale)
}

const applyLocale = locale => {
  i18n.global.locale.value = locale
  document.documentElement.lang = locale
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // The choice then lasts for this visit only.
  }
}

/**
 * Tells the API the signed-in user's language, which it uses for the e-mails it sends
 * them (the language lives in the browser otherwise). Only when it changed since the last
 * successful call — or always with `force`, right after a sign-in, since the last call may
 * have been for another user of this browser. Never blocking: a failure just means a
 * retry on the next start.
 */
export const syncServerLanguage = async (locale, { force = false } = {}) => {
  try {
    if (!localStorage.getItem('token')) return
    if (!force && localStorage.getItem(SYNCED_KEY) === locale) return
    await localizationService.updateMyLanguage(locale)
    localStorage.setItem(SYNCED_KEY, locale)
  } catch (err) {
    console.warn('[i18n] Could not store the language on the server', err)
  }
}

/** Switches the UI language, loading its texts first so nothing flashes as a raw key. */
export const setLocale = async locale => {
  if (!loadedLocales.has(locale)) {
    await loadLocaleMessages(locale)
  }
  applyLocale(locale)
  syncServerLanguage(locale)
}

/** Called once before the app mounts. Never throws: a failed load leaves keys on screen. */
export const initI18n = async () => {
  const locale = readStoredLocale()
  try {
    await loadLocaleMessages(locale)
  } catch (err) {
    console.error(`[i18n] Could not load texts for "${locale}"`, err)
  }
  applyLocale(locale)
  syncServerLanguage(locale)
}

export default i18n
